"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EVENT_SECTION_FALLBACK_MESSAGE = exports.preserveUnknownEventKeys = exports.unknownEventKeys = exports.serializeEventDefinition = exports.orderSectionsLikeRunner = exports.eventInputOf = exports.normalizeEventDefinition = void 0;
const text = (value) => value === null || value === undefined ? "" : String(value);
const isoOrNull = (value) => {
    if (value === null || value === undefined || value === "")
        return null;
    const date = value instanceof Date ? value : new Date(text(value));
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};
const settingsOf = (start) => {
    const nested = start.collSettings;
    return nested && typeof nested === "object" ? nested : {};
};
const multicolumnOf = (settings, index, warnings) => {
    const rules = Array.isArray(settings.rules) ? settings.rules : [];
    const out = [];
    for (const rule of rules) {
        if (rule &&
            typeof rule === "object" &&
            rule.type === "multicolumn") {
            out.push(text(rule.fields)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean));
        }
        else {
            warnings.push({ code: "unknown_rule", index });
        }
    }
    return out;
};
const nonEmptyIds = (value) => Array.isArray(value) ? value.map(text).filter(Boolean) : [];
const apiTableOf = (first) => {
    if (!first || typeof first !== "object")
        return null;
    const settings = settingsOf(first);
    const ids = Array.isArray(settings.eventTableAPIs)
        ? settings.eventTableAPIs.map(text).filter(Boolean)
        : [];
    if (ids.length === 0)
        return null;
    return {
        sourceIds: ids,
        merge: !!settings.mergeEventTable,
        download: !!settings.downloadEventTableBtn,
    };
};
const normalizeEventDefinition = (doc) => {
    const warnings = [];
    let items = [];
    if (Array.isArray(doc.fields))
        items = doc.fields;
    else
        warnings.push({ code: "fields_not_array" });
    const groups = new Map();
    items.forEach((raw, index) => {
        var _a;
        if (!raw || typeof raw !== "object" || !text(raw.collectionID)) {
            warnings.push({ code: "unknown_item", index });
            return;
        }
        const key = text(raw.collectionID);
        const group = (_a = groups.get(key)) !== null && _a !== void 0 ? _a : [];
        group.push({ item: raw, index });
        groups.set(key, group);
    });
    let dropdown = null;
    const sections = [];
    for (const [collectionId, group] of groups) {
        const head = group[0].item;
        if (head.dropdownLabel && head.dropdownCollectionIDs) {
            if (dropdown) {
                warnings.push({ code: "extra_dropdown", index: group[0].index });
                continue;
            }
            dropdown = {
                label: text(head.dropdownLabel),
                collectionIds: text(head.dropdownCollectionIDs)
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            continue;
        }
        let current = null;
        const subGroups = [];
        for (const entry of group) {
            if (entry.item.field === "") {
                current = { start: entry, rows: [] };
                subGroups.push(current);
            }
            else if (!current) {
                warnings.push({
                    code: "item_before_section_start",
                    index: entry.index,
                });
            }
            else {
                current.rows.push(entry);
            }
        }
        subGroups.forEach((sub, n) => {
            const start = sub.start.item;
            const settings = settingsOf(start);
            if (sub.start.index > 0 &&
                (nonEmptyIds(settings.eventTableAPIs).length > 0 ||
                    nonEmptyIds(start.eventTableAPIs).length > 0))
                warnings.push({ code: "api_table_not_first", index: sub.start.index });
            const fieldIds = [];
            for (const row of sub.rows) {
                if (row.item.field)
                    fieldIds.push(text(row.item.field));
                else
                    warnings.push({ code: "title_dropped", index: row.index });
            }
            sections.push({
                key: `${collectionId}:${n}`,
                collectionId,
                fieldIds,
                insert: !!start.insert || !!settings.insert,
                update: !!start.update || !!settings.update,
                multiple: !!start.multiple || !!settings.multiple,
                collapsible: !!settings.collapsable,
                collapseOnLoad: !!settings.collapseonload,
                multicolumn: multicolumnOf(settings, sub.start.index, warnings),
                sharesRecordWith: n === 0 ? null : `${collectionId}:0`,
                hiddenByDropdown: false,
            });
        });
    }
    if (dropdown) {
        const controlled = new Set(dropdown.collectionIds);
        for (const section of sections)
            section.hiddenByDropdown = controlled.has(section.collectionId);
    }
    return {
        id: text(doc._id),
        canvasId: text(doc.projectID),
        name: text(doc.name),
        version: isoOrNull(doc.lastUpdateDate),
        perms: doc.perms,
        sections,
        dropdown,
        apiTable: apiTableOf(items[0]),
        warnings,
    };
};
exports.normalizeEventDefinition = normalizeEventDefinition;
const eventInputOf = (def) => ({
    name: def.name,
    sections: def.sections.map((s) => ({
        collectionId: s.collectionId,
        fieldIds: [...s.fieldIds],
        insert: s.insert,
        update: s.update,
        multiple: s.multiple,
        collapsible: s.collapsible,
        collapseOnLoad: s.collapseOnLoad,
        multicolumn: s.multicolumn.map((rule) => [...rule]),
    })),
    dropdown: def.dropdown
        ? {
            label: def.dropdown.label,
            collectionIds: [...def.dropdown.collectionIds],
        }
        : null,
    apiTable: def.apiTable
        ? {
            sourceIds: [...def.apiTable.sourceIds],
            merge: def.apiTable.merge,
            download: def.apiTable.download,
        }
        : null,
});
exports.eventInputOf = eventInputOf;
const orderSectionsLikeRunner = (sections) => {
    var _a;
    const groups = new Map();
    for (const section of sections) {
        const group = (_a = groups.get(section.collectionId)) !== null && _a !== void 0 ? _a : [];
        group.push(section);
        groups.set(section.collectionId, group);
    }
    return [...groups.values()].flat();
};
exports.orderSectionsLikeRunner = orderSectionsLikeRunner;
const serializeEventDefinition = (input) => {
    const items = [];
    const apiIds = input.apiTable ? nonEmptyIds(input.apiTable.sourceIds) : [];
    (0, exports.orderSectionsLikeRunner)(input.sections).forEach((section, position) => {
        var _a, _b;
        const withTable = position === 0 && apiIds.length > 0;
        items.push({
            collectionID: section.collectionId,
            field: "",
            collSettings: {
                insert: section.insert,
                update: section.update,
                multiple: section.multiple,
                collapsable: section.collapsible,
                collapseonload: section.collapseOnLoad,
                downloadEventTableBtn: withTable ? !!((_a = input.apiTable) === null || _a === void 0 ? void 0 : _a.download) : false,
                mergeEventTable: withTable ? !!((_b = input.apiTable) === null || _b === void 0 ? void 0 : _b.merge) : false,
                rules: section.multicolumn.map((ids) => ({
                    type: "multicolumn",
                    fields: ids.join(","),
                })),
                eventTableAPIs: withTable ? apiIds : [],
            },
        });
        for (const fieldId of section.fieldIds)
            items.push({ collectionID: section.collectionId, field: fieldId });
    });
    if (input.dropdown)
        items.push({
            collectionID: `dropdown-${items.length}`,
            dropdownLabel: input.dropdown.label,
            dropdownCollectionIDs: input.dropdown.collectionIds.join(","),
        });
    return items;
};
exports.serializeEventDefinition = serializeEventDefinition;
const START_KEYS = new Set([
    "collectionID",
    "field",
    "collSettings",
    "insert",
    "update",
    "multiple",
]);
const SETTINGS_KEYS = new Set([
    "insert",
    "update",
    "multiple",
    "collapsable",
    "collapseonload",
    "downloadEventTableBtn",
    "mergeEventTable",
    "rules",
    "eventTableAPIs",
]);
const RULE_KEYS = new Set(["type", "fields"]);
const ROW_KEYS = new Set(["collectionID", "field"]);
const DROPDOWN_KEYS = new Set([
    "collectionID",
    "dropdownLabel",
    "dropdownCollectionIDs",
]);
const NO_KEYS = new Set();
const classifyItems = (items) => {
    const startCount = new Map();
    let dropdownSeen = false;
    return items.map((raw) => {
        var _a;
        if (!raw || typeof raw !== "object")
            return { kind: "item" };
        const item = raw;
        const collectionId = text(item.collectionID);
        if (!collectionId)
            return { kind: "item" };
        if (item.dropdownLabel && item.dropdownCollectionIDs) {
            if (dropdownSeen)
                return { kind: "item" };
            dropdownSeen = true;
            return { kind: "dropdown" };
        }
        if (item.field === "") {
            const n = (_a = startCount.get(collectionId)) !== null && _a !== void 0 ? _a : 0;
            startCount.set(collectionId, n + 1);
            return { kind: "start", section: `${collectionId}:${n}` };
        }
        const count = startCount.get(collectionId);
        if (count === undefined || !item.field)
            return { kind: "item" };
        return {
            kind: "row",
            match: `${collectionId}:${count - 1}|${text(item.field)}`,
        };
    });
};
const unknownOf = (obj, known) => {
    const out = {};
    if (!obj || typeof obj !== "object" || Array.isArray(obj))
        return out;
    for (const [key, value] of Object.entries(obj))
        if (!known.has(key))
            out[key] = value;
    return out;
};
const rulesOf = (item) => {
    const rules = settingsOf(item).rules;
    return Array.isArray(rules) ? rules : [];
};
const describeKeys = (prefix, unknown) => Object.entries(unknown).map(([key, value]) => { var _a; return `${prefix}.${key}=${(_a = JSON.stringify(value)) !== null && _a !== void 0 ? _a : String(value)}`; });
const unknownEventKeys = (fields) => {
    if (!Array.isArray(fields))
        return [];
    const kinds = classifyItems(fields);
    const out = [];
    fields.forEach((raw, index) => {
        const kind = kinds[index];
        const item = raw;
        if (kind.kind === "item") {
            out.push(...describeKeys("item", unknownOf(raw, NO_KEYS)));
        }
        else if (kind.kind === "row") {
            out.push(...describeKeys("row", unknownOf(item, ROW_KEYS)));
        }
        else if (kind.kind === "dropdown") {
            out.push(...describeKeys("dropdown", unknownOf(item, DROPDOWN_KEYS)));
        }
        else {
            out.push(...describeKeys("start", unknownOf(item, START_KEYS)));
            out.push(...describeKeys("settings", unknownOf(item.collSettings, SETTINGS_KEYS)));
            for (const rule of rulesOf(item))
                out.push(...describeKeys("rule", unknownOf(rule, RULE_KEYS)));
        }
    });
    return out.sort();
};
exports.unknownEventKeys = unknownEventKeys;
const withExtra = (target, extra) => {
    const out = Object.assign({}, target);
    if (extra)
        for (const [key, value] of Object.entries(extra))
            if (!(key in out))
                out[key] = value;
    return out;
};
const preserveUnknownEventKeys = (stored, next) => {
    const starts = new Map();
    const rows = new Map();
    let dropdown;
    if (Array.isArray(stored)) {
        const storedKinds = classifyItems(stored);
        stored.forEach((raw, index) => {
            const kind = storedKinds[index];
            const item = raw;
            if (kind.kind === "start")
                starts.set(kind.section, {
                    top: unknownOf(item, START_KEYS),
                    settings: unknownOf(item.collSettings, SETTINGS_KEYS),
                    rules: rulesOf(item).map((rule) => unknownOf(rule, RULE_KEYS)),
                });
            else if (kind.kind === "row")
                rows.set(kind.match, unknownOf(item, ROW_KEYS));
            else if (kind.kind === "dropdown")
                dropdown = unknownOf(item, DROPDOWN_KEYS);
        });
    }
    const kinds = classifyItems(next);
    return next.map((item, index) => {
        const kind = kinds[index];
        if (kind.kind === "row")
            return withExtra(item, rows.get(kind.match));
        if (kind.kind === "dropdown")
            return withExtra(item, dropdown);
        const from = kind.kind === "start" ? starts.get(kind.section) : undefined;
        if (!from)
            return Object.assign({}, item);
        const out = withExtra(item, from.top);
        const settings = item.collSettings;
        if (settings && typeof settings === "object" && !Array.isArray(settings)) {
            const merged = withExtra(settings, from.settings);
            if (Array.isArray(merged.rules))
                merged.rules = merged.rules.map((rule, position) => rule && typeof rule === "object" && !Array.isArray(rule)
                    ? withExtra(rule, from.rules[position])
                    : rule);
            out.collSettings = merged;
        }
        return out;
    });
};
exports.preserveUnknownEventKeys = preserveUnknownEventKeys;
exports.EVENT_SECTION_FALLBACK_MESSAGE = "This section could not be saved.";
